import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import { CatmullRomCurve3, Vector3 } from 'three';
import useStore from '../../store/useStore';

/**
 * Camera path keyframes — one Vector3 per story beat.
 *
 * Beat 0 (scroll 0.00): Wide opening shot, both rings centred
 * Beat 1 (scroll 0.25): Drift left, close-up on ring A
 * Beat 2 (scroll 0.50): Pull back, dramatic angle
 * Beat 3 (scroll 0.75): Rise and orbit — dark mode territory
 * Beat 4 (scroll 1.00): Settle into configurator shot
 */
const CAMERA_PATH = new CatmullRomCurve3([
    new Vector3(0,    0,    14),   // 0 – hero
    new Vector3(-3.5, 0.5, 10),   // 1 – ring A close
    new Vector3(0,    2,    12),   // 2 – overhead tilt
    new Vector3(3,    1.5,   9),   // 3 – ring B, dark transition
    new Vector3(0,    0.5,  11),   // 4 – configurator
], false, 'catmullrom', 0.5);

/**
 * Target path — where the camera looks at each beat.
 * Parallels CAMERA_PATH index-for-index.
 */
const TARGET_PATH = new CatmullRomCurve3([
    new Vector3(0,   0,    0),
    new Vector3(-2,  0,    0),
    new Vector3(0,   0,    0),
    new Vector3(2,   0,    0),
    new Vector3(0,  -0.2,  0),
], false, 'catmullrom', 0.5);

const _pos = new Vector3();
const _target = new Vector3();

/**
 * Reads `scrollProgress` from the store every frame and moves the camera
 * along the pre-defined Catmull-Rom spline.  Uses `useFrame` so it runs
 * inside the R3F render loop without triggering React renders.
 */
export default function CameraRig() {
    const { camera } = useThree();
    const prevProgress = useRef(0);

    useFrame(() => {
        const progress = useStore.getState().scrollProgress;

        // Skip if progress hasn't meaningfully changed
        if (Math.abs(progress - prevProgress.current) < 0.0001) return;
        prevProgress.current = progress;

        CAMERA_PATH.getPoint(progress, _pos);
        TARGET_PATH.getPoint(progress, _target);

        camera.position.lerp(_pos, 0.06);
        camera.lookAt(_target);
    });

    return null;
}
